import type {Label as LabelPrimitive} from 'radix-ui';
import {Slot} from 'radix-ui';
import * as React from 'react';
import type {ControllerProps, FieldPath, FieldValues} from 'react-hook-form';
import {Controller, FormProvider, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  InputDescription,
  InputItem,
  InputLabel,
  InputMessage,
} from '@/components/ui/input';
import {TooltipWrapper} from '@/components/ui/tooltip';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{name: props.name}}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const {getFieldState, formState} = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const {id} = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
);

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({...props}, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{id}}>
      <InputItem ref={ref} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

type FormLabelProps = React.ComponentPropsWithoutRef<
  typeof LabelPrimitive.Root
> & {
  required?: boolean;
};

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  FormLabelProps
>(({children, className, required, ...props}, ref) => {
  const {formItemId} = useFormField();
  const {t} = useTranslation();

  return (
    <InputLabel ref={ref} className={className} htmlFor={formItemId} {...props}>
      {children}
      {required && (
        <TooltipWrapper title={t('Required field')}>
          <span
            aria-label={t('Required field')}
            className="ml-1 inline-flex cursor-help text-danger-600"
          >
            *
          </span>
        </TooltipWrapper>
      )}
    </InputLabel>
  );
});
FormLabel.displayName = 'FormLabel';

function FormRequiredLegend() {
  const {t} = useTranslation();

  return (
    <p className="text-2xs font-medium text-secondary-dark">
      <span className="text-danger-600">*</span> {t('Required field')}
    </p>
  );
}

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot.Slot>,
  React.ComponentPropsWithoutRef<typeof Slot.Slot> & {
    render?: (field: ReturnType<typeof useFormField>) => React.ReactNode;
  }
>(({render, ...props}, ref) => {
  const field = useFormField();

  if (render) {
    return render(field);
  }

  const {error, formItemId, formDescriptionId, formMessageId} = field;

  return (
    <Slot.Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({...props}, ref) => {
  const {formDescriptionId} = useFormField();

  return <InputDescription ref={ref} id={formDescriptionId} {...props} />;
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({children, ...props}, ref) => {
  const {error, formMessageId} = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <InputMessage ref={ref} id={formMessageId} {...props}>
      {body}
    </InputMessage>
  );
});
FormMessage.displayName = 'FormMessage';

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
  // eslint-disable-next-line react-refresh/only-export-components
  useFormField,
};
